import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { scheduleOptions } from '../../../../utils/data/data';
import Select from '../../../_common/Form/Selects/Select/Select';
import Checkbox from '../../../_common/Form/Checkbox/Checkbox';
import InputField from '../../../_common/Form/TextFields/InputField';
import classes from './addEditProduct.module.scss';
import Button from '../../../_common/Form/Buttons/Button';
import api from '../../../../api/api';
import utils from '../../../../utils/utils';
import { getProductsRequest } from '../../../../store/actions/products';
import Loader from '../../../_common/Loader/Loader';
import { useQuery } from '../../../../utils/hooks/useQuery';
import SelectWithSearch from '../../../_common/Form/Selects/SelectWithSearch/SelectWithSearch';
import Api from '../../../../api/api';
import accountHelpers from '../../../../utils/accountHelpers';

const viewData = [
  {
    name: 'Name',
    type: 'input',
    path: 'name',
  },
  {
    name: 'NDC',
    type: 'input',
    path: 'NDC',
  },
  {
    name: 'Schedule',
    type: 'select',
    path: 'schedule',
    options: scheduleOptions,
  },
  {
    name: 'Dosage',
    type: 'input',
    path: 'dosage',
  },
  {
    name: 'Form',
    type: 'input',
    path: 'form',
  },
  {
    name: 'Unit of Measure',
    type: 'input',
    path: 'unitMeasure',
  },
  {
    name: 'Package Size',
    type: 'input',
    path: 'packageSize',
    validation: 'currency',
  },
  {
    name: 'Quantity',
    type: 'input',
    path: 'quantity',
    validation: 'currency',
  },
  {
    name: 'Unit Cost',
    type: 'input',
    path: 'unitPrice',
    validation: 'currency',
  },
  {
    name: 'Total Cost',
    type: 'input',
    path: 'cost',
    validation: 'currency',
  },
  {
    name: 'Drug type',
    type: 'select',
    path: 'brand',
    options: ['Brand', 'Generic', 'Immunotherapy', 'Biosimilars'],
  },
  {
    name: 'Incentives',
    type: 'input',
    path: 'incentives',
  },
  {
    name: 'Refrigerated',
    type: 'checkbox',
    path: 'refrigerated',
  },
  {
    name: 'Type',
    type: 'input',
    path: 'type',
  },
];

const costFields = ['quantity', 'unitPrice', 'packageSize'];

const calculateCost = ({ unitPrice, quantity, packageSize }) => {
  if (!unitPrice || !quantity || !packageSize) return null;

  const cost = (unitPrice / packageSize) * quantity;

  return Math.round((Number.isFinite(cost) ? cost : 0) * 100) / 100;
};

const AddEditProduct = ({ edit }) => {
  const { productId } = useParams();
  const { query } = useQuery();
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const productList = useSelector((store) => store.products.productList);

  const timeout = useRef();

  const [requestData, setRequestData] = useState({
    name: '',
    NDC: '',
    schedule: '',
    dosage: '',
    form: '',
    unitMeasure: '',
    packageSize: '',
    quantity: '',
    cost: '',
    unitPrice: '',
    refrigerated: false,
    type: '',
    brand: '',
    incentives: '',
    manufacturerId: '',
    active: false,
    specialty: false,
  });
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);

  const [nameError, setNameError] = useState('');

  const [searchValue, setSearchValue] = useState('');
  const [manufacturers, setManufacturers] = useState([]);
  const [searchManufacturerLoading, setSearchManufacturerLoading] = useState(false);

  const { active, specialty } = requestData;

  useEffect(() => {
    if (edit) {
      (async () => {
        setLoading(true);

        await dispatch(getProductsRequest({ productId, otc: query.otc }));
      })();
    }
  }, [edit]);

  useEffect(() => {
    if (edit && productList.length === 1) {
      (async () => {
        await setRequestData({
          name: productList[0].name,
          NDC: productList[0].NDC,
          schedule: productList[0].schedule,
          dosage: productList[0].dosage,
          incentives: productList[0].incentives,
          form: productList[0].form,
          unitMeasure: productList[0].unitMeasure,
          packageSize: productList[0].packageSize,
          quantity: productList[0].quantity,
          cost: productList[0].cost,
          refrigerated: productList[0].refrigerated,
          type: productList[0].type,
          active: productList[0].active,
          specialty: productList[0].specialty,
          brand: productList[0].brand,
          unitPrice: productList[0].unitPrice,
          manufacturerId: productList[0].manufacturerId || '',
        });

        if (productList[0].manufacturerId) {
          const { data: { data } } = await Api.getUserList({
            searcherId: accountHelpers.getUser().id,
            userId: productList[0].manufacturerId,
          });

          const manufacturerName = data.response?.[0]?.name;

          setSearchValue(manufacturerName);
        }
      })();

      setLoading(false);
    }
  }, [productList]);

  const changeData = (value, path, searchVal) => {
    if (searchVal) {
      setSearchValue(searchVal);
    }

    if (path === 'name') {
      setNameError('');
    }

    setRequestData((prev) => {
      const newData = { ...prev, [path]: value.id || value };

      if (costFields.includes(path)) {
        const currentCost = calculateCost(newData);

        if (currentCost !== null) newData.cost = currentCost;
      }

      return newData;
    });
  };

  const sendData = async () => {
    if (!requestData.name.trim()) {
      setNameError('Field is required');

      toast.error('Please fill this field');
    } else {
      setSaveLoading(true);

      const newRequest = { ...requestData };

      if (!newRequest.incentives) newRequest.incentives = 0;
      if (!newRequest.manufacturerId) newRequest.manufacturerId = null;

      let data;
      try {
        if (edit) {
          data = await api.editProduct({ ...newRequest, id: productId });
        } else {
          data = await api.addProduct(newRequest);
        }

        // if (data.statusText === 'OK') {
        navigate('/products');
        // }
      } catch (e) {
        console.warn(e);
      }

      setSaveLoading(false);
    }
  };

  const selectSearch = (value) => {
    setSearchValue(value);

    clearTimeout(timeout.current);

    if (value.trim()) {
      setSearchManufacturerLoading(true);

      timeout.current = setTimeout(async () => {
        const { data: { data } } = await Api.getUserList({
          searcherId: accountHelpers.getUser().id,
          name: value,
          role: 'Manufacturer',
          limit: 100,
        });

        setManufacturers(data.response);

        setSearchManufacturerLoading(false);
      }, 500);
    } else {
      setManufacturers([]);
      setSearchManufacturerLoading(false);
    }
  };

  const onKeyPress = useCallback((e, validationType, isPaste) => {
    if (validationType === 'currency') {
      utils.keyPressCurrency(e, isPaste, undefined, 2);
    } else {
      () => {};
    }
  }, []);

  return (
    edit && loading ? <Loader className={classes.loader} />
      : (
        <div className={classes.wrapper}>
          <h2>
            {edit ? 'Edit' : 'Add'}
            {' '}
            Product
          </h2>

          {viewData.map((d) => (
            d.type === 'select'
              ? (
                <div key={d.name}>
                  <Select
                    label={d.name}
                    onChange={(option) => changeData(option, d.path)}
                    currentOption={requestData[d.path]}
                    options={d.options}
                  />
                </div>
              )
              : d.type === 'checkbox'
                ? (
                  <div key={d.name}>
                    <Checkbox
                      onChange={(checked) => changeData(checked, d.path)}
                      label={d.name}
                      checked={requestData[d.path]}
                    />
                  </div>
                )
                : (
                  <div key={d.name}>
                    <InputField
                      label={d.name}
                      error={d.path === 'name' && nameError}
                      value={requestData[d.path]}
                      onChange={(value) => changeData(value, d.path)}
                      onKeyPress={(e) => onKeyPress(e, d.validation)}
                      onPaste={(e) => onKeyPress(e, d.validation, true)}
                    />
                  </div>
                )
          ))}

          <SelectWithSearch
            label="Manufacturer"
            onChange={(value) => selectSearch(value)}
            loading={searchManufacturerLoading}
            options={manufacturers}
            onSelectOption={(option, value) => changeData(option, 'manufacturerId', value)}
            searchValue={searchValue}
            optionPathname="name"
            optionKey=""
            zIndex={31}
          />

          <div className={classes.checkbox}>
            <div>
              <Checkbox
                onChange={(checked) => changeData(checked, 'active')}
                label="Active"
                checked={active}
              />
            </div>

            <div>
              <Checkbox
                onChange={(checked) => changeData(checked, 'specialty')}
                label="Specialty"
                checked={specialty}
              />
            </div>
          </div>

          <div className={classes.btn_wrapper}>
            <Button
              onClick={sendData}
              title={edit ? 'Save' : 'Add product'}
              type="button"
              className={classes.btn}
              addButton={!edit}
              reverseColor
              loading={saveLoading}
            />

            <Button
              href="/products"
              title="Cancel"
              type="button"
              className={classes.btn}
              clearButton
            />
          </div>
        </div>
      )
  );
};

AddEditProduct.propTypes = {
  edit: PropTypes.bool,
};

AddEditProduct.defaultProps = {
  edit: false,
};

export default AddEditProduct;
